import { useCallback, useEffect, useRef, useState } from "react";

import { Email } from "../../../factories/emails/models/emails.models";
import { useAppSelector } from "../../../redux/redux";
import {
  AiEmailInsights,
  AiEmailTriageItem,
  useAiEmailInsightsMutation,
  useAiEmailTriageMutation,
} from "../../../slices/AiSlice";
import { aiErrorMessage } from "../../ai/ai.utils";
import { useAiStatus } from "../../ai/useAiStatus";
import {
  earlierWithSamePerson,
  loadReads,
  loadTags,
  saveReads,
  saveTags,
  shouldAutoRead,
  toAiListItem,
  toAiMessage,
} from "./mailPage.utils";

/**
 * Mail page AI. Tags: the inbox emails on screen are sorted once (one AI use
 * per new batch); results are remembered in this browser. Read: the open
 * email's summary, next steps and quick replies, also remembered, and fetched
 * automatically only while the person has uses to spare.
 */
export const useMailAi = ({
  inboxEmails,
  openEmail,
  loaded,
  clientEmails,
  canTag,
  canRead,
}: {
  inboxEmails: Email[];
  openEmail: Email | null;
  loaded: Email[];
  /** Sender addresses known to be clients (helps the sorting). */
  clientEmails: Set<string>;
  canTag: boolean;
  canRead: boolean;
}) => {
  const { user } = useAppSelector((state) => state.appReducer);
  const userId: string = user?.id || "";
  const myEmail: string = user?.nylasToken?.email || "";
  const { isOn, orgId, status } = useAiStatus();
  const [triage] = useAiEmailTriageMutation();
  const [insights] = useAiEmailInsightsMutation();

  const [tags, setTags] = useState<
    Record<string, AiEmailTriageItem & { at: number }>
  >(() => loadTags(userId));
  const [reads, setReads] = useState<
    Record<string, AiEmailInsights & { at: number }>
  >(() => loadReads(userId));
  const [tagging, setTagging] = useState(false);
  const [reading, setReading] = useState<string | null>(null);
  const [readError, setReadError] = useState<{
    id: string;
    message: string;
  } | null>(null);
  const asked = useRef(new Set<string>());

  useEffect(() => {
    setTags(loadTags(userId));
    setReads(loadReads(userId));
  }, [userId]);

  // ----- Sort the inbox (once per email) -----
  const inboxKey = inboxEmails.map((e) => e.id).join(",");
  useEffect(() => {
    if (!isOn || !canTag || !orgId) return;
    const todo = inboxEmails
      .filter((e) => !tags[e.id] && !asked.current.has(e.id))
      .slice(0, 30);
    if (todo.length === 0) return;
    todo.forEach((e) => asked.current.add(e.id));
    setTagging(true);
    triage({
      orgId,
      emails: todo.map((e) =>
        toAiListItem(
          e,
          clientEmails.has((e.from?.[0]?.email || "").toLowerCase()),
        ),
      ),
    })
      .unwrap()
      .then((res) => {
        setTags((prev) => {
          const next = { ...prev };
          res.items.forEach((item) => {
            next[item.id] = { ...item, at: Date.now() };
          });
          saveTags(userId, next);
          return next;
        });
      })
      .catch(() => {
        // Sorting is a nice-to-have: without it the list just has no tags.
      })
      .finally(() => setTagging(false));
  }, [inboxKey, isOn, canTag, orgId]);

  // ----- Read one email -----
  const read = useCallback(
    (email: Email, force = false) => {
      if (
        !isOn ||
        !orgId ||
        (!force && (reads[email.id] || reading === email.id))
      )
        return;
      setReading(email.id);
      setReadError(null);
      insights({
        orgId,
        email: toAiMessage(email, myEmail),
        thread: earlierWithSamePerson(email, loaded, myEmail).map((e) =>
          toAiMessage(e, myEmail),
        ),
      })
        .unwrap()
        .then((res) => {
          setReads((prev) => {
            const next = { ...prev, [email.id]: { ...res, at: Date.now() } };
            saveReads(userId, next);
            return next;
          });
          // The read also tells us the email's tag.
          setTags((prev) => {
            if (prev[email.id]) return prev;
            const next = {
              ...prev,
              [email.id]: {
                id: email.id,
                intent: res.intent,
                hasDocuments: res.hasDocuments,
                brief: "",
                at: Date.now(),
              },
            };
            saveTags(userId, next);
            return next;
          });
        })
        .catch((error) =>
          setReadError({ id: email.id, message: aiErrorMessage(error) }),
        )
        .finally(() => setReading((r) => (r === email.id ? null : r)));
    },
    [isOn, orgId, reads, reading, loaded, myEmail, userId],
  );

  const autoRead = shouldAutoRead(status?.usedToday, status?.limitPerDay);
  useEffect(() => {
    if (
      openEmail &&
      canRead &&
      autoRead &&
      !reads[openEmail.id] &&
      readError?.id !== openEmail.id
    )
      read(openEmail);
  }, [openEmail?.id, canRead, isOn]);

  return {
    isOn,
    tags,
    tagging,
    read,
    reading: !!openEmail && reading === openEmail.id,
    insight: openEmail ? reads[openEmail.id] : undefined,
    readError:
      openEmail && readError?.id === openEmail.id ? readError.message : null,
    autoRead,
  };
};
